import React, {useEffect} from "react";
import {useDispatch} from "react-redux";
import {getApprovalProcessById} from "../../../api/approval/ApprovalProcessService";
import {setApprovalProcess} from "../../../redux/slice/contentSlice";
import ReadOnlyInputBasic from "../../../components/input/ReadOnlyInputBasic";
import {DailyCheckChildren} from "./dailyCheck/subPages/DailyCheckChildren";
import GroupCodeApprovalFields from "../groupCode/GroupCodeApprovalFields";
import Th_title from "../../../components/Th_title";
import ReportCodeFormTable from "./ReportCodeFormTable";

function EditSection({title, children, required = false}) {
    return (
        <section className="user-account-edit-form__section">
            <h3 className="user-account-edit-form__section-title">
                {title}
                {required && <span className="c-red ml-1">*</span>}
            </h3>
            <div className="user-account-edit-form__section-body">{children}</div>
        </section>
    );
}

function DailyCheckCodeEditorPanel({
    item,
    setItem,
    children,
    setChildren,
    readOnly = false,
    onSave,
    onDelete,
    hideFormActions = false,
}) {
    const dispatch = useDispatch();
    const canEdit = !readOnly && (item?._isNew || item?.auth?.update !== false);
    const isCreateLayout = Boolean(item?._isNew && hideFormActions);

    useEffect(() => {
        if (!item?.approvalProcessId) {
            dispatch(setApprovalProcess({}));
            return;
        }

        getApprovalProcessById(item.approvalProcessId).then((response) => {
            dispatch(setApprovalProcess({...response.data}));
        });
    }, [dispatch, item?.approvalProcessId, item?.id, item?._isNew]);

    if (!item) {
        return (
            <div className="code-editor-empty">
                <div className="code-editor-empty__icon">⌘</div>
                <p className="code-editor-empty__title">코드를 선택하세요</p>
                <p className="code-editor-empty__desc">왼쪽 목록에서 코드를 선택하거나 상단 등록 버튼으로 추가하세요.</p>
            </div>
        );
    }

    const nameField = canEdit ? (
        <input
            className="basic-input input-style"
            value={item.name ?? ""}
            onChange={(e) => setItem((prev) => ({...prev, name: e.target.value}))}
        />
    ) : (
        <ReadOnlyInputBasic target="name" value={item.name} />
    );

    const etcField = canEdit ? (
        <input
            className="basic-input input-style"
            value={item.etc ?? ""}
            onChange={(e) => setItem((prev) => ({...prev, etc: e.target.value}))}
        />
    ) : (
        <ReadOnlyInputBasic target="etc" value={item.etc} />
    );

    if (isCreateLayout) {
        return (
            <div className="code-editor code-editor--wide report-code-editor report-code-editor--create">
                <div className="report-code-editor__create-scroll user-account-edit-form user-account-edit-form--embedded">
                    <EditSection title="기본정보">
                        <ReportCodeFormTable>
                            <tbody>
                                <tr>
                                    <Th_title title="일일체크코드 명" colspan={1} className="fieldset-td" required auth>
                                        {nameField}
                                    </Th_title>
                                    <Th_title title="내용" colspan={1} className="fieldset-td" auth>
                                        {etcField}
                                    </Th_title>
                                </tr>
                            </tbody>
                        </ReportCodeFormTable>
                    </EditSection>

                    <EditSection title="일일체크코드 관리 항목">
                        <div className="report-code-editor__panel-body report-code-editor__panel-body--padded">
                            <DailyCheckChildren
                                children={children}
                                setChildren={setChildren}
                                view={!canEdit}
                            />
                        </div>
                    </EditSection>

                    <EditSection title="결재라인">
                        <div className="report-code-editor__panel-body report-code-editor__panel-body--padded">
                            {canEdit ? (
                                <GroupCodeApprovalFields code={item} showAfter={false} />
                            ) : (
                                <div className="code-editor__read-row">
                                    <span className="code-editor__read-label">결재라인</span>
                                    <span className="code-editor__read-value">{item.approvalProcessName ?? "—"}</span>
                                </div>
                            )}
                        </div>
                    </EditSection>
                </div>
            </div>
        );
    }

    return (
        <div className="code-editor code-editor--wide report-code-editor">
            <span className="code-editor__meta">일일체크 설정</span>

            {item.useCount > 0 && (
                <p className="report-code-editor__notice">
                    사용 건수 {item.useCount}건 — 사용 중인 코드는 수정·삭제할 수 없습니다.
                </p>
            )}

            <div className="report-code-editor__section">
                <div className="code-editor__field">
                    <label>
                        일일체크코드 명<span className="required">*</span>
                    </label>
                    {nameField}
                </div>

                <div className="code-editor__field">
                    <label>내용</label>
                    {etcField}
                </div>
            </div>

            <div className="report-code-editor__children">
                <p className="report-code-editor__children-label">일일체크코드 관리 항목</p>
                <DailyCheckChildren
                    children={children}
                    setChildren={setChildren}
                    view={!canEdit}
                />
            </div>

            <div className="report-code-editor__section">
                {canEdit ? (
                    <GroupCodeApprovalFields code={item} showAfter={false} />
                ) : (
                    <div className="code-editor__read-row">
                        <span className="code-editor__read-label">결재라인</span>
                        <span className="code-editor__read-value">{item.approvalProcessName ?? "—"}</span>
                    </div>
                )}
            </div>

            {!hideFormActions && (canEdit || (!item._isNew && item.useCount === 0)) && (
                <div className="code-editor__actions">
                    {!item._isNew && item.useCount === 0 && onDelete && (
                        <button type="button" className="code-manage-delete-btn" onClick={onDelete}>
                            삭제
                        </button>
                    )}
                    {canEdit && onSave && (
                        <button type="button" className="save-btn" onClick={onSave}>
                            {item._isNew ? "등록" : "저장"}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default DailyCheckCodeEditorPanel;
